import { eventChannel } from 'redux-saga';
import { all, call, cancel, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import io from 'socket.io-client';
import { getFormValues } from 'redux-form';

import { AuthProps, getAuthProps } from '@topconagriculture/auth';
import { WidgetType } from '@topconagriculture/dashboard/build/components/Widget/widget.types';
import { WidgetDTO } from '@topconagriculture/dashboard/build/api/widget/widget.types';

import { config } from '../../config';
import {
  NotificationEvent,
  NotificationEventContent,
  NotificationStatus,
  NotificationType,
} from './notifications.types';
import { getUserById } from '../user/user.actions';
import { PresetDateRanges, notificationsConfig } from '../../config/constants';
import {
  notificationError,
  notificationSuccess,
  notificationWarning,
} from '../../core/helpers/utils/notificationUtils';
import { getAllOperators } from '../entities/operators/operators.actions';
import { getAllIngredientsRequest } from '../entities/ingredients/ingredients.actions';
import { getRecipeList } from '../entities/recipes/recipes.actions';
import { getPremixList } from '../entities/premixes/premixes.actions';
import { getAllGroups } from '../entities/group/group.actions';
import { getFeedingLoads } from '../application/loadMixers/loadMixers.actions';
import { getMixersList, getMixerStatus } from '../entities/mixers/mixers.actions';
import { farmsSelector, presetSelector } from '../selectors';
import { farmActions } from '../actions';
import { getHerdManagementSystem } from '../entities/hms/hms.actions';
import { getSettingsManage } from '../entities/settings/settings.actions';
import { getFeedingPeriodList } from '../entities/feeding/feeding.actions';
import { getLotTypes } from '../entities/lotTypes/lotTypes.actions';
import { getLotMarketTypes } from '../entities/lotMarketTypes/lotMarketTypes.actions';
import { getMilkings } from '../entities/dataTools/dataTools.actions';
import { getSelectedTimeRange } from '../entities/dataTools/dataTools.selector';
import { getWeighbridgeTicketList } from '../entities/weighbridge/weighbridge.actions';
import { setDashboardExclamation } from '../entities/dashboard/dashboard.actions';
import { dashboardPaths } from '../../routes/Dashboard/dashboardPaths';
import { fetchOrderList } from '../entities/orders/orders.actions';
import { getReportDataByPreset } from '../entities/reports/reports.actions';
import { InventoryReportType } from '../entities/reports/reports.constants';
import { Forms } from '../form/form.selector';
import { getWidgets } from '../entities/dashboard/dashboard.selector';
import { toLocalDate } from '../../core/helpers/utils/dateUtils';
import { getRelations } from '../entities/relations/relations.actions';

const notifierByStatus = {
  [NotificationStatus.Completed]: notificationSuccess,
  [NotificationStatus.Failed]: notificationError,
  [NotificationStatus.Warning]: notificationWarning,
};

export const notificationsSocketFactory = (subscribers: string[], topics: NotificationType[]): Promise<any> => {
  const socket = io(config.NOTIFICATIONS_HOSTNAME, {
    path: config.NOTIFICATION_SUBSCRIPTION_PATH,
    transports: ['polling', 'websocket'],
  });

  return new Promise((resolve, reject) => {
    socket.on('connect', () => {
      socket.emit('subscribeTopics', { subscribers, topics });
      resolve(socket);
    });
    socket.on('connect_error', () => {
      socket.disconnect();
      reject(socket);
    });
  });
};

export const constructNotificationMessage = (content: NotificationEventContent) => {
  return `${content.message} ${content.annotation || ''} (device: ${content.device})`;
};

export function* notificationEventSaga(payload: NotificationEvent) {
  const content = payload.content || {};
  const notifier = notifierByStatus[content.status] || notificationError;

  yield put(
    notifier({
      ...notificationsConfig.defaultConf,
      title: constructNotificationMessage(content),
      autoDismiss: 10,
    }),
  );
}

export function* updateIngredientsSaga() {
  yield all([
    yield put(getAllIngredientsRequest.started({})),
    yield put(getRecipeList.started({})),
    yield put(getPremixList.started({})),
    yield put(getAllGroups.started({})),
    yield put(getFeedingLoads.started({})),
  ]);
}

export function* updateRationsSaga() {
  yield all([
    yield put(getRecipeList.started({})),
    yield put(getPremixList.started({})),
    yield put(getAllGroups.started({})),
    yield put(getFeedingLoads.started({})),
  ]);
}

export function* updatePremixesSaga() {
  yield all([
    yield put(getRecipeList.started({})),
    yield put(getPremixList.started({})),
    yield put(getAllGroups.started({})),
    yield put(getFeedingLoads.started({})),
  ]);
}

export function* updateBatchesSaga() {
  yield null;
}

export function* updateDriversSaga() {
  yield put(getAllOperators.started({}));
}

export function* updateGrouptypesSaga() {
  yield null;
}

export function* updateGroupsSaga() {
  yield all([yield put(getAllGroups.started({})), yield put(getFeedingPeriodList.started({}))]);
}

export function* updateMixersSaga() {
  yield put(getMixersList.started({}));
  yield take(getMixersList.done);
  yield put(getFeedingLoads.started({}));
}

export function* updateParamsSaga() {
  yield put(getSettingsManage.started({}));
}

export function* updatePeriodsSaga() {
  yield put(getFeedingPeriodList.started({}));
}

export function* updateHMSGroupsSaga() {
  yield put(getHerdManagementSystem.started({}));
}

export function* updateLotTypeSaga() {
  yield put(getLotTypes.started({}));
}

export function* updateLotMarketTypeSaga() {
  yield put(getLotMarketTypes.started({}));
}

export function* updateMixerStatusSaga(payload: NotificationEvent) {
  yield put(getMixerStatus.started(payload.content.mixerId));
}

function* updateMilkProductionSaga() {
  const selectedTimeRange = yield select(getSelectedTimeRange);
  if (selectedTimeRange) {
    yield put(getMilkings.started(selectedTimeRange, { silent: true }));
  }
}

function* updateWeighbridgeTicketsSaga() {
  yield put(getWeighbridgeTicketList.started({ status: 'open' }));
}

function* showExclamationSaga(hasWidget, payload) {
  if (window.location.pathname !== dashboardPaths.root && payload.content.newAlert && hasWidget) {
    yield put(setDashboardExclamation(true));
  }
}

function* updateWidgetInventorySaga(payload) {
  const widgets: WidgetDTO[] = yield select(getWidgets);
  const hasWidget = !!widgets.find((widget) => widget.type === WidgetType.INVENTORY);

  yield call(showExclamationSaga, hasWidget, payload);
}
function* updateWidgetDeliveriesDeviationSaga(payload) {
  const widgets: WidgetDTO[] = yield select(getWidgets);
  const hasWidget = !!widgets.find((widget) => widget.type === WidgetType.DELIVER_DEVIATION);

  yield call(showExclamationSaga, hasWidget, payload);
}

function* updateWidgetLoadDeviationSaga(payload) {
  const widgets: WidgetDTO[] = yield select(getWidgets);
  const hasWidget = !!widgets.find((widget) => widget.type === WidgetType.LOAD_DEVIATION);

  yield call(showExclamationSaga, hasWidget, payload);
}

function* updateStockTransactionSaga(param) {
  yield put(fetchOrderList.started({}));

  const formValues = yield select(getFormValues(Forms.reportsPageForm));
  const preset = yield select(presetSelector.getStockTransactionPreset);
  const payload = {
    ...preset,
    dataSettings: {
      ...preset.dataSettings,
      endDate:
        preset.dataSettings.dateRangeType === PresetDateRanges.LAST_MONTH
          ? undefined
          : toLocalDate(new Date(preset.dataSettings.endDate!)).toISOString(),
    },
  };
  yield put(
    getReportDataByPreset.started({
      preset: formValues ? formValues.settings : payload,
      type: InventoryReportType.StockTransaction,
    }),
  );
  yield put(getAllIngredientsRequest.started({}));
}

function* updateRelationsSaga() {
  yield put(getRelations.started({}));
}

export function* subscribeChannelSaga(socket: any) {
  return eventChannel((emit) => {
    const handler = (payload: NotificationEvent) => emit(payload);

    socket.on(NotificationType.Send, handler);
    socket.on(NotificationType.Receive, handler);
    socket.on(NotificationType.INGREDIENT_UPDATED, handler);
    socket.on(NotificationType.RATION_UPDATED, handler);
    socket.on(NotificationType.PREMIX_UPDATED, handler);
    socket.on(NotificationType.BATCH_UPDATED, handler);
    socket.on(NotificationType.DRIVER_UPDATED, handler);
    socket.on(NotificationType.GROUPTYPE_UPDATED, handler);
    socket.on(NotificationType.GROUP_UPDATED, handler);
    socket.on(NotificationType.MIXER_UPDATED, handler);
    socket.on(NotificationType.PARAMS_UPDATED, handler);
    socket.on(NotificationType.PERIOD_UPDATED, handler);
    socket.on(NotificationType.HMSGROUP_UPDATED, handler);
    socket.on(NotificationType.LOTTYPE_UPDATED, handler);
    socket.on(NotificationType.LOTMARKETTYPE_UPDATED, handler);
    socket.on(NotificationType.MIXERSTATUS_UPDATED, handler);
    socket.on(NotificationType.MILKING_UPDATED, handler);
    socket.on(NotificationType.MILKPRICE_UPDATED, handler);
    socket.on(NotificationType.CONCENTRATE_UPDATED, handler);
    socket.on(NotificationType.WEIGHBRIDGE_TICKET_UPDATED, handler);
    socket.on(NotificationType.WIDGET_INVENTORY, handler);
    socket.on(NotificationType.WIDGET_LOADS, handler);
    socket.on(NotificationType.WIDGET_DELIVERIES, handler);
    socket.on(NotificationType.STOCK_TRANSACTION_UPDATED, handler);
    socket.on(NotificationType.RELATION_UPDATED, handler);

    return function* close() {
      yield call(socket.disconnect);
    };
  });
}

const sagaByType = {
  [NotificationType.Send]: notificationEventSaga,
  [NotificationType.Receive]: notificationEventSaga,
  [NotificationType.INGREDIENT_UPDATED]: updateIngredientsSaga,
  [NotificationType.RATION_UPDATED]: updateRationsSaga,
  [NotificationType.PREMIX_UPDATED]: updatePremixesSaga,
  [NotificationType.BATCH_UPDATED]: updateBatchesSaga,
  [NotificationType.DRIVER_UPDATED]: updateDriversSaga,
  [NotificationType.GROUPTYPE_UPDATED]: updateGrouptypesSaga,
  [NotificationType.GROUP_UPDATED]: updateGroupsSaga,
  [NotificationType.MIXER_UPDATED]: updateMixersSaga,
  [NotificationType.PARAMS_UPDATED]: updateParamsSaga,
  [NotificationType.PERIOD_UPDATED]: updatePeriodsSaga,
  [NotificationType.HMSGROUP_UPDATED]: updateHMSGroupsSaga,
  [NotificationType.LOTTYPE_UPDATED]: updateLotTypeSaga,
  [NotificationType.LOTMARKETTYPE_UPDATED]: updateLotMarketTypeSaga,
  [NotificationType.MIXERSTATUS_UPDATED]: updateMixerStatusSaga,
  [NotificationType.MILKING_UPDATED]: updateMilkProductionSaga,
  [NotificationType.WEIGHBRIDGE_TICKET_UPDATED]: updateWeighbridgeTicketsSaga,
  [NotificationType.WIDGET_INVENTORY]: updateWidgetInventorySaga,
  [NotificationType.WIDGET_DELIVERIES]: updateWidgetDeliveriesDeviationSaga,
  [NotificationType.WIDGET_LOADS]: updateWidgetLoadDeviationSaga,
  [NotificationType.STOCK_TRANSACTION_UPDATED]: updateStockTransactionSaga,
  [NotificationType.RELATION_UPDATED]: updateRelationsSaga,
};

export function* notificationsListenSocketSaga(socket: any) {
  const channel = yield call(subscribeChannelSaga, socket);

  while (true) {
    const payload: NotificationEvent = yield take(channel);
    const saga = sagaByType[payload.type];
    if (saga) {
      yield call(saga, payload);
    }
  }
}

export function* recieveAndSendNotificationsSaga() {
  try {
    const user: AuthProps = yield select(getAuthProps);
    const socket = yield call(
      notificationsSocketFactory,
      [user.companyId, user.userId],
      [NotificationType.Send, NotificationType.Receive],
    );
    yield fork(notificationsListenSocketSaga, socket);
  } catch (err) {
    yield put(notificationError('app:socket_connection_error'));
    cancel();
  }
}

export function* updateEntietiesNotificationsSaga() {
  try {
    const farm = yield select(farmsSelector.getSelectedFarm);
    const socket = yield call(
      notificationsSocketFactory,
      [farm.uniqueId],
      [
        NotificationType.INGREDIENT_UPDATED,
        NotificationType.RATION_UPDATED,
        NotificationType.PREMIX_UPDATED,
        NotificationType.BATCH_UPDATED,
        NotificationType.DRIVER_UPDATED,
        NotificationType.GROUPTYPE_UPDATED,
        NotificationType.GROUP_UPDATED,
        NotificationType.MIXER_UPDATED,
        NotificationType.PARAMS_UPDATED,
        NotificationType.PERIOD_UPDATED,
        NotificationType.HMSGROUP_UPDATED,
        NotificationType.LOTTYPE_UPDATED,
        NotificationType.LOTMARKETTYPE_UPDATED,
        NotificationType.MIXERSTATUS_UPDATED,
        NotificationType.MILKING_UPDATED,
        NotificationType.MILKPRICE_UPDATED,
        NotificationType.CONCENTRATE_UPDATED,
        NotificationType.WEIGHBRIDGE_TICKET_UPDATED,
        NotificationType.WIDGET_INVENTORY,
        NotificationType.WIDGET_LOADS,
        NotificationType.WIDGET_DELIVERIES,
        NotificationType.STOCK_TRANSACTION_UPDATED,
        NotificationType.RELATION_UPDATED,
      ],
    );
    yield fork(notificationsListenSocketSaga, socket);
  } catch (err) {
    yield put(notificationError('app:socket_connection_error'));
    cancel();
  }
}

export function* watchNotificationsSaga() {
  yield all([yield takeLatest(getUserById.done, recieveAndSendNotificationsSaga)]);
  yield all([yield takeLatest(farmActions.getFarmsListRequest.done, updateEntietiesNotificationsSaga)]);
}
